import type { ReactNode } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  ChartBarIcon,
  CreditCardIcon,
  GearIcon,
  GiftIcon,
  HouseIcon,
  MedalIcon,
  QuestionIcon,
  ReceiptIcon,
  SignOutIcon,
  SquaresFourIcon,
  TicketIcon,
  UserIcon,
  UsersIcon,
} from '@phosphor-icons/react';
import { useAuth } from '../../../shared/ui/AuthProvider';
import { UserRole } from '../../../shared/utils/types';

interface NavItem {
  label: string;
  to: string;
  icon: ReactNode;
  exact?: boolean;
  roles?: UserRole[];
}

interface AppSidebarProps {
  open: boolean;
  onNavigate: () => void;
}

const ICON_SIZE = 20;

export const AppSidebar = ({ open, onNavigate }: AppSidebarProps) => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const mainItems: NavItem[] = [
    {
      label: t('app.nav.dashboard'),
      to: '/',
      icon: <HouseIcon size={ICON_SIZE} />,
      exact: true,
    },
    {
      label: t('app.nav.payRent'),
      to: '/pay-rent',
      icon: <CreditCardIcon size={ICON_SIZE} />,
    },
    {
      label: t('app.nav.payments'),
      to: '/payments',
      icon: <ReceiptIcon size={ICON_SIZE} />,
    },
    {
      label: t('app.nav.rewards'),
      to: '/rewards/history',
      icon: <GiftIcon size={ICON_SIZE} />,
    },
    {
      label: t('app.nav.profile'),
      to: '/profile',
      icon: <UserIcon size={ICON_SIZE} />,
    },
    {
      label: t('app.nav.settings'),
      to: '/settings',
      icon: <GearIcon size={ICON_SIZE} />,
    },
  ];

  const adminRoles = [UserRole.ADMIN, UserRole.MODERATOR];
  const adminItems: NavItem[] = [
    {
      label: t('header.adminDashboard'),
      to: '/admin',
      icon: <SquaresFourIcon size={ICON_SIZE} />,
      exact: true,
      roles: adminRoles,
    },
    {
      label: t('header.manageCoupons'),
      to: '/admin/coupons',
      icon: <TicketIcon size={ICON_SIZE} />,
      roles: adminRoles,
    },
    {
      label: t('header.rewardRules'),
      to: '/admin/reward-rules',
      icon: <MedalIcon size={ICON_SIZE} />,
      roles: adminRoles,
    },
    {
      label: t('header.rewardPointsConfig'),
      to: '/admin/reward-points',
      icon: <ChartBarIcon size={ICON_SIZE} />,
      roles: adminRoles,
    },
    {
      label: t('header.userRewards'),
      to: '/admin/rewards',
      icon: <GiftIcon size={ICON_SIZE} />,
      roles: adminRoles,
    },
    {
      label: t('header.manageUsers'),
      to: '/admin/users',
      icon: <UsersIcon size={ICON_SIZE} />,
      roles: [UserRole.ADMIN],
    },
  ].filter(item => item.roles.includes(user?.role as UserRole));

  const renderItem = (item: NavItem) => (
    <Link
      key={item.to}
      to={item.to}
      className="mc-nav"
      activeOptions={{ exact: item.exact ?? false }}
      activeProps={{ className: 'mc-nav mc-nav--active' }}
      onClick={onNavigate}
    >
      <span className="mc-nav__icon">{item.icon}</span>
      {item.label}
    </Link>
  );

  const handleLogout = async () => {
    onNavigate();
    await logout();
    navigate({ to: '/login' });
  };

  return (
    <aside
      className={`mc-sidebar ${open ? 'mc-sidebar--open' : ''}`}
      aria-label={t('app.nav.menu')}
    >
      {mainItems.map(renderItem)}

      {adminItems.length > 0 && (
        <>
          <span className="mc-sidebar__section">{t('app.nav.admin')}</span>
          {adminItems.map(renderItem)}
        </>
      )}

      <div className="mc-help">
        <strong>
          <QuestionIcon size={18} weight="bold" />
          {t('app.help.title')}
        </strong>
        <p>{t('app.help.text')}</p>
        <a
          className="mc-btn mc-btn--outline mc-btn--small"
          href="mailto:info@themietclub.com"
        >
          {t('app.help.contact')}
        </a>
      </div>

      <button
        type="button"
        className="mc-nav mc-sidebar__logout"
        onClick={handleLogout}
      >
        <span className="mc-nav__icon">
          <SignOutIcon size={ICON_SIZE} />
        </span>
        {t('app.nav.logout')}
      </button>
    </aside>
  );
};
